import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditTrailVisualization = ({ className = '' }) => {
  const [auditData, setAuditData] = useState([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [selectedActivity, setSelectedActivity] = useState('all');
  const [viewMode, setViewMode] = useState('timeline');

  useEffect(() => {
    const mockAuditData = [
      {
        id: 'audit-001',
        timestamp: new Date('2024-09-19T14:30:00'),
        user: 'Dr. Sarah Chen',
        role: 'QA Manager',
        activity: 'document_update',
        description: 'Updated SOP-QC-001 Manufacturing Quality Control Procedures',
        documentId: 'SOP-QC-001',
        blockchainHash: '0x7f9fade1c0d57a7af66ab4ead79fade1c0d57a7af66ab4ead7c2c2eb7b11a91385',
        verified: true,
        impact: 'medium',
        category: 'Quality Control'
      },
      {
        id: 'audit-002',
        timestamp: new Date('2024-09-19T13:45:00'),
        user: 'Dr. Michael Rodriguez',
        role: 'Lab Technician',
        activity: 'test_result',
        description: 'Recorded batch test results for Batch QA-2024-1247',
        documentId: 'BTR-2024-1247',
        blockchainHash: '0x8a1bfade2d1e68b8g77bc5fbe80gade2d1e68b8g77bc5fbe81d3d3fc8c22b02496',
        verified: true,
        impact: 'high',
        category: 'Batch Testing'
      },
      {
        id: 'audit-003',
        timestamp: new Date('2024-09-19T12:15:00'),
        user: 'Dr. Elena Vasquez',
        role: 'Regulatory Officer',
        activity: 'submission_update',
        description: 'Modified EMA submission SUB-2024-001 for Cardiomax 50mg',
        documentId: 'SUB-2024-001',
        blockchainHash: '0x9b2cgade3e2f79c9h88cd6gce91hade3e2f79c9h88cd6gce92e4e4gd9d33c13507',
        verified: true,
        impact: 'high',
        category: 'Regulatory Submission'
      },
      {
        id: 'audit-004',
        timestamp: new Date('2024-09-19T11:30:00'),
        user: 'Dr. James Wilson',
        role: 'Equipment Specialist',
        activity: 'calibration',
        description: 'Completed calibration of HPLC System #3',
        documentId: 'CAL-HPLC-003',
        blockchainHash: '0xac3dhade4f3g80daj99de7hdf02iade4f3g80daj99de7hdf03f5f5he0e44d24618',
        verified: true,
        impact: 'medium',
        category: 'Equipment'
      },
      {
        id: 'audit-005',
        timestamp: new Date('2024-09-19T10:45:00'),
        user: 'Dr. Lisa Thompson',
        role: 'Safety Officer',
        activity: 'adverse_event',
        description: 'Created adverse event report AER-2024-089',
        documentId: 'AER-2024-089',
        blockchainHash: '0xbd4eiade5g4h91ebk00ef8ieg13jbde5g4h91ebk00ef8ieg14g6g6if1f55e35729',
        verified: true,
        impact: 'high',
        category: 'Safety Reporting'
      },
      {
        id: 'audit-006',
        timestamp: new Date('2024-09-19T09:20:00'),
        user: 'System',
        role: 'Automated Process',
        activity: 'backup',
        description: 'Automated daily backup of compliance database completed',
        documentId: 'BACKUP-20240919',
        blockchainHash: '0xce5fjbde6h5i02fck11fg9jfh24kcde6h5i02fck11fg9jfh25h7h7jg2g66f46830',
        verified: true,
        impact: 'low',
        category: 'System Maintenance'
      }
    ];

    setAuditData(mockAuditData);
  }, [selectedTimeRange]);

  const getActivityIcon = (activity) => {
    switch (activity) {
      case 'document_update': return 'FileText';
      case 'test_result': return 'FlaskConical';
      case 'submission_update': return 'Send';
      case 'calibration': return 'Settings';
      case 'adverse_event': return 'AlertTriangle';
      case 'backup': return 'Database';
      default: return 'Activity';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getImpactBgColor = (impact) => {
    switch (impact) {
      case 'high': return 'bg-error/10';
      case 'medium': return 'bg-warning/10';
      case 'low': return 'bg-success/10';
      default: return 'bg-muted';
    }
  };

  const filteredData = auditData?.filter(item => {
    if (selectedActivity !== 'all' && item?.activity !== selectedActivity) return false;
    return true;
  });

  const formatTime = (timestamp) => {
    return timestamp?.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    return timestamp?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const truncateHash = (hash) => {
    return `${hash?.substring(0, 10)}...${hash?.substring(hash?.length - 8)}`;
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Audit Trail Visualization</h2>
          <p className="text-sm text-muted-foreground">Blockchain-verified activity tracking</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          
          <select
            value={selectedActivity}
            onChange={(e) => setSelectedActivity(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
          >
            <option value="all">All Activities</option>
            <option value="document_update">Document Updates</option>
            <option value="test_result">Test Results</option>
            <option value="submission_update">Submissions</option>
            <option value="calibration">Calibrations</option>
            <option value="adverse_event">Adverse Events</option>
          </select>
          
          <div className="flex items-center border border-border rounded-md">
            <button
              onClick={() => setViewMode('timeline')}
              className={`px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                viewMode === 'timeline' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Timeline
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                viewMode === 'table' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Table
            </button>
          </div>
        </div>
      </div>
      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>
            
            <div className="space-y-6">
              {filteredData?.map((item, index) => (
                <div key={item?.id} className="relative flex items-start space-x-6">
                  {/* Timeline Dot */}
                  <div className={`relative z-10 flex items-center justify-center w-16 h-16 rounded-full border-4 border-card ${getImpactBgColor(item?.impact)}`}>
                    <Icon 
                      name={getActivityIcon(item?.activity)} 
                      size={20} 
                      className={getImpactColor(item?.impact)}
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0 pb-6">
                    <div className="bg-muted/30 rounded-lg p-4 border border-border">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-sm font-medium text-card-foreground mb-1">
                            {item?.description}
                          </h3>
                          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                            <span>{item?.user} ({item?.role})</span>
                            <span>{formatDate(item?.timestamp)} at {formatTime(item?.timestamp)}</span>
                            <span className="px-2 py-1 bg-secondary/10 text-secondary rounded-full">
                              {item?.category}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getImpactBgColor(item?.impact)} ${getImpactColor(item?.impact)}`}>
                            {item?.impact?.toUpperCase()}
                          </span>
                          {item?.verified && (
                            <div className="flex items-center space-x-1 text-success">
                              <Icon name="Shield" size={14} />
                              <span className="text-xs font-medium">Verified</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Document ID:</span>
                          <span className="font-mono text-card-foreground">{item?.documentId}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Blockchain Hash:</span>
                          <span className="font-mono text-primary">{truncateHash(item?.blockchainHash)}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-end space-x-2 mt-3">
                        <Button variant="outline" size="xs">
                          View Details
                        </Button>
                        <Button variant="outline" size="xs">
                          Verify on Blockchain
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Time</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">User</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Activity</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Document</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Impact</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-card-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData?.map((item) => (
                  <tr key={item?.id} className="border-b border-border hover:bg-muted/20 transition-colors duration-150">
                    <td className="p-4">
                      <div className="text-sm text-card-foreground">{formatTime(item?.timestamp)}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(item?.timestamp)}</div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-card-foreground">{item?.user}</div>
                      <div className="text-xs text-muted-foreground">{item?.role}</div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <Icon name={getActivityIcon(item?.activity)} size={16} className="text-muted-foreground" />
                        <div>
                          <div className="text-sm text-card-foreground">{item?.activity?.replace('_', ' ')?.toUpperCase()}</div>
                          <div className="text-xs text-muted-foreground">{item?.category}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm font-mono text-card-foreground">{item?.documentId}</div>
                      <div className="text-xs font-mono text-muted-foreground">{truncateHash(item?.blockchainHash)}</div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getImpactBgColor(item?.impact)} ${getImpactColor(item?.impact)}`}>
                        {item?.impact?.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4">
                      {item?.verified ? (
                        <div className="flex items-center space-x-1 text-success">
                          <Icon name="Shield" size={14} />
                          <span className="text-xs font-medium">Verified</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1 text-warning">
                          <Icon name="Clock" size={14} />
                          <span className="text-xs font-medium">Pending</span>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-1">
                        <Button variant="ghost" size="xs">
                          <Icon name="Eye" size={14} />
                        </Button>
                        <Button variant="ghost" size="xs">
                          <Icon name="ExternalLink" size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {filteredData?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No audit trail entries match your current filters</p>
        </div>
      )}
    </div>
  );
};

export default AuditTrailVisualization;